import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Checkbox, Select, Space, Tag, Typography, theme as antdTheme } from 'antd';
import { space, useNarrowViewport } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpHint, HintLine } from './HelpHint';
import { MatchDetailsDrawer } from './MatchDetailsDrawer';
import { MergePolicyControl } from './MergePolicyControl';
import { OwnerPicker, ownerLabel } from './OwnerPicker';
import { byDisplayName } from './sortRows';
import { isReady } from './useCrmQueue';
import type {
  CrmOwner,
  LeadEditPatch,
  MatchBucket,
  MergePolicy,
  PersonPreview,
  PreviewScope,
  PreviewSummary,
} from './types';
import type { CrmQueueAction, CrmQueueState } from './useCrmQueue';

/**
 * The buckets Match lists FROM THE PREVIEW: `new` fills its right column,
 * `existing` and `ambiguous` its left, and nothing else is drawn. By the time
 * a preview row reaches this predicate, `needs_email` no longer appears here
 * on this axis — the live read is the bucketing authority, and it re-buckets
 * even a match-check `needs_email` person (see `isInPreviewSelection` below)
 * as `new` (with `emailChecked: false`) or whatever else it finds. A
 * genuinely still-emailless preview row (the live read agreeing with the
 * check) is `needs_email` too and, same as ever, drawn in neither column.
 *
 * Exported because two other places must agree with it exactly. `MatchView`
 * itself partitions `previews` with it into the two columns below. The tab's
 * label counts only the matched subset (`isMatched` below) — the size of the
 * Already-in-your-CRM column — so `alreadyRows.length` is what
 * `CrmViews.test.tsx` pins against the reported count.
 *
 * Do NOT use this to choose which people `CrmViews` asks the preview
 * about — that is `isInPreviewSelection`'s job, and it runs over
 * match-check rows, a different input, at a point before the preview has
 * had any chance to re-bucket anyone.
 */
export function isInMatchQueue(row: { bucket: MatchBucket }): boolean {
  return row.bucket === 'new' || row.bucket === 'existing' || row.bucket === 'ambiguous';
}

/**
 * The rows the Match tab's NUMBER counts: the people found in the CRM, one
 * match (`existing`) or several (`ambiguous`). `new` rows are listed too — the
 * right-hand column — but they are the tab's work-in-waiting, not its subject:
 * the tab is named for matching, and its count says how many matches there
 * are (gh-31 D4). Kept apart from `isInMatchQueue`, which still decides what
 * is previewed and listed; narrowing THAT would stop New rows being previewed
 * at all and leave Send with nothing to create.
 *
 * This definition itself needed no emailless-specific case — a preview row
 * is a preview row, and this predicate has always counted any of them
 * bucketed `existing`/`ambiguous`, full stop. What DID need care (fix round
 * 1) is upstream of here: an emailless `needs_email` match-check row goes
 * through the preview's id-property walk same as everyone else, and CAN
 * legitimately come back `existing` or `ambiguous` if that walk finds a real
 * link — e.g. one created by an earlier Send. That row is genuinely IN the
 * CRM by the time the preview lands, and this predicate is right to count
 * it. The trap was on the COLD side: match-check buckets an emailless person
 * `needs_email` unconditionally, before it ever consults links, so a cold
 * `existing + ambiguous` (the pre-preview approximation of this predicate,
 * in `CrmViews`) cannot rule out that one of them is secretly already
 * matched — trusting it would make the tab number jump upward the moment
 * the preview lands and this predicate (correctly) starts counting that row.
 * `CrmViews`' `matchCount` is the fix: it refuses to guess (`null`) on any
 * cold draw where match-check reports a `needs_email` row at all, and
 * defers to this predicate, unconditionally, once the live read is in.
 */
export function isMatched(row: { bucket: MatchBucket }): boolean {
  return row.bucket === 'existing' || row.bucket === 'ambiguous';
}

/**
 * Who `CrmViews` asks the (expensive) live preview about — a match-check
 * row, not a preview row. Widened past `isInMatchQueue` to also include
 * `needs_email`: match-check still buckets an emailless person that way,
 * deliberately, because Clean and the rail badge read that bucket straight
 * off match-check and must keep seeing it. But the preview is the authority
 * on bucketing, re-reads the CRM, and answers for emailless people too —
 * always with `emailChecked: false` (matched by the VendoIQ id property
 * instead of by email), but the resulting bucket is whatever that id walk
 * actually finds: `new` on no hit, same as anyone else never seen before,
 * but `existing`/`ambiguous` on a real hit (e.g. a link created by an
 * earlier Send) — same as anyone else the CRM already knows about. Either
 * way they carry their notes and tasks same as anyone else. (This is why
 * `CrmViews`' cold `matchCount` cannot trust `existing + ambiguous` off
 * match-check alone whenever a `needs_email` row is in play — see its doc
 * comment.)
 *
 * Deliberately a separate function rather than a widened `isInMatchQueue`:
 * that predicate keeps meaning "what Match draws from the preview response"
 * everywhere else it's read (`MatchView`'s own partition, its doc comment
 * above), and a `needs_email` match-check row is never itself drawn — only
 * the preview row the backend returns IN ITS PLACE is.
 */
export function isInPreviewSelection(row: { bucket: MatchBucket }): boolean {
  return isInMatchQueue(row) || row.bucket === 'needs_email';
}

export interface MatchViewProps {
  previews: PersonPreview[]; // from the parent's preview query
  owners: CrmOwner[]; // from the parent's owners query ([] on failure)
  canManage: boolean;
  fieldChoices: CrmQueueState['fieldChoices'];
  ownerPicks: CrmQueueState['ownerPicks'];
  /**
   * The policy this run would commit under (`policyOverride ??` the tenant
   * default). Forwarded verbatim to `MatchDetailsDrawer`, which uses it to
   * pre-select the side an UNPICKED conflict would actually land on.
   * Optional — undefined (preview not in yet) reads as `fillBlanksOnly`.
   */
  effectivePolicy?: MergePolicy;
  /** `PreviewResponse.policy` — the tenant default the control falls back to
   *  while `effectivePolicy` is still unknown. Defaults to the safe policy. */
  tenantPolicy?: MergePolicy;
  /**
   * `PreviewResponse.summary` — drives the above-the-columns "N already
   * sent" disclosure (p3 Task 5, spec 4 §5a). Undefined until the preview
   * lands, same as `SendView`'s identical prop; `summary.collapsed` is
   * itself an optional deploy-skew field (an older backend's response won't
   * carry it), so absence and `0` render identically: nothing.
   */
  summary?: PreviewSummary;
  /**
   * The scope `previews`/`summary` were actually fetched under
   * (`CrmQueueState.previewScope`) — load-bearing for the delta disclosure,
   * NOT decorative.
   *
   * The backend computes `summary.collapsed` UNCONDITIONALLY, including
   * under `scope: 'all'`, where it names the count that WOULD have been
   * collapsed so a client can offer the disclosure without a second request
   * (viq-backend `crmUpload.ts`'s preview handler, and its test "scope:
   * 'all' computes collapsed but does NOT narrow who gets previewed"). So
   * `collapsed > 0` alone does NOT mean anything is hidden: under `'all'`
   * every one of those rows is on screen. Gating the line on this prop is
   * what keeps it from claiming N leads are hidden while the operator is
   * looking at them — and what retires the "Show them" button once it has
   * been pressed, instead of leaving it dispatching into a state
   * `previewScope` is already in (same query key, no refetch, dead button).
   *
   * Defaults to `'changed'` — `useCrmQueue`'s own initial state, so a caller
   * that omits it gets exactly a fresh queue's behavior.
   */
  previewScope?: PreviewScope;
  /** The preview's `overwriteAllowed === false` (spec 2026-09-28 §5.8); locks the policy control and the drawer's picks. */
  overwriteLocked?: boolean;
  /**
   * The live CRM read behind this view is still in flight and `previews` is
   * empty because of it — not because there is nobody. Opening Match is what
   * STARTS that read, so this is the first thing an operator sees here on a
   * cold draw, and each column says so in place of its empty state.
   */
  loading?: boolean;
  /**
   * Saves a VendoIQ field from the Details drawer, then the parent re-reads
   * the CRM. Absent → read-only drawer.
   */
  onEditLead?: (personId: string, patch: LeadEditPatch) => Promise<void>;
  /**
   * Whether this view is the one currently on screen. Match and Send stay
   * MOUNTED across a stage-tab switch (only `display` toggles), but the
   * Details drawer portals to `document.body`, so without this it would
   * keep showing over Send/Clean/Leads too. `true` by default — every
   * existing caller/test that never passes it keeps today's behavior.
   */
  active?: boolean;
  dispatch: React.Dispatch<CrmQueueAction>;
}

interface MatchColumnProps {
  title: string;
  count: number;
  emptyState: string;
  testId: string;
  /** Left/primary (the matched people — Match's subject) or right/secondary
   *  (the New people — final prep). Sets the heading level and colour. */
  emphasis: 'primary' | 'secondary';
  /** Always-visible statement of what this column IS, pinned with the header
   *  rather than placed in the scroller, so it can't scroll out of reach. */
  hint: { text: string; testId: string };
  /** The owner rule, stated once per column — the rows below repeat an
   *  OwnerPicker each, and a "?" on every one of them would bury the rows. */
  headerHelp: { label: string; text: string; testId: string };
  /** Controls that belong to this column, pinned in its header with the hint
   *  (the New column's bulk-assign toolbar). */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * One of Match's two side-by-side panels. The BODY is the scroller, not the
 * panel, so a column's header and count stay pinned while its rows move —
 * which is the point of the split: the operator compares a New person against
 * the CRM group without scrolling past every New row to reach it.
 *
 * The panel's surface deliberately mirrors `MetricCard` (container background
 * + secondary border + `token.borderRadius`) so both columns read as the same
 * family as the KPI tiles above them, and follow light/dark for free.
 *
 * `role="region"` + `tabIndex={0}`: a scrollable box that can't be focused
 * hides its overflow from anyone without a pointer (WCAG 2.1.1), and browsers
 * only started focusing scrollers on their own very recently.
 */
function MatchColumn({
  title,
  count,
  emptyState,
  testId,
  emphasis,
  hint,
  headerHelp,
  toolbar,
  children,
}: MatchColumnProps) {
  const { token } = antdTheme.useToken();
  // Colon-free: `useId` values are not valid CSS selector syntax, and
  // `aria-labelledby` sends this one through an accessible-name computation
  // that resolves it as one.
  const headingId = `match-col-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div
      data-testid={`match-panel-${testId.replace('match-column-', '')}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        // Load-bearing: a bare `1fr` track is `minmax(auto, 1fr)`, so without
        // this one long unbroken name refuses to shrink and blows the grid out.
        minWidth: 0,
        maxHeight: 'min(60vh, 640px)',
        borderRadius: token.borderRadius,
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          padding: `${space.md}px ${space.lg}px`,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Space size={space.xs} align="center">
          {/* The scroll region below is named by this id, so it must wrap ONLY
              the heading text — the hint and the help trigger beside it would
              otherwise be read out as part of the region's name. */}
          <div id={headingId}>
            <Typography.Title
              level={emphasis === 'primary' ? 4 : 5}
              style={{ margin: 0, color: emphasis === 'secondary' ? token.colorTextSecondary : undefined }}
            >
              {`${title} (${count})`}
            </Typography.Title>
          </div>
          <HelpHint
            label={headerHelp.label}
            text={headerHelp.text}
            testId={headerHelp.testId}
            placement="bottom"
          />
        </Space>
        <HintLine text={hint.text} testId={hint.testId} style={{ marginTop: space.xs, marginBottom: 0 }} />
        {toolbar && <div style={{ marginTop: space.sm }}>{toolbar}</div>}
      </div>
      <div
        data-testid={testId}
        role="region"
        aria-labelledby={headingId}
        tabIndex={0}
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          // A flick that hits the end of a column otherwise chains into the
          // page scroller and yanks the whole Batch screen.
          overscrollBehavior: 'contain',
          padding: space.lg,
        }}
      >
        {count === 0 ? <Typography.Text type="secondary">{emptyState}</Typography.Text> : children}
      </div>
    </div>
  );
}

/**
 * Match view (p3 Task 4) — buckets `existing`+`ambiguous` into the LEFT,
 * primary column and `new` into the RIGHT, secondary one (gh-31 D10), each an
 * independently scrollable `MatchColumn`, with a Details drawer
 * (`MatchDetailsDrawer`) for `existing` rows, read off the live previews so a
 * refetch re-renders it, an `OwnerPicker` on every owner-eligible
 * row, and a green Ready-to-send tag on each New row that `isReady` — the
 * same predicate Send uses, so the two tabs can never disagree. The bulk
 * "assign selected" toolbar is pinned in the New column's header, for space,
 * rather than sitting at the top of the view — but the checkboxes it acts on
 * still span both columns; any owner-eligible row, New or Already, can be
 * ticked and assigned in one go. `ambiguous` rows are deliberately
 * control-free (multiple CRM records share the email — nothing is changed
 * for them here) and sit in the Already-in-CRM column alongside `existing`,
 * as they always have.
 *
 * `canManage === false`: every group/row/diff/value stays visible — browsing
 * is never blocked — but the Details drawer's radios and OwnerPicker selects
 * are disabled. The one exception is the bulk-assign checkbox column: with no
 * `manage:CrmIntegration`, checkboxes (and the now-unusable toolbar built
 * around them) don't render at all, rather than rendering inert.
 */
/**
 * What this send will add to the CRM's activity timeline for one person.
 *
 * Renders nothing when the backend sent no count — absent means "nothing to
 * add", which is why this checks for the field rather than for a zero. It is
 * the only thing on screen for a person whose every field already matches:
 * their comparison is empty, and the timeline entry IS the send.
 */
function ActivityLine({ preview }: { preview: PersonPreview }) {
  const a = preview.activitiesToPush;
  if (!a || a.calls + a.tasks === 0) return null;
  return (
    <Typography.Text type="secondary" data-testid={`match-activities-${preview.personId}`}>
      {CRM_COPY.activity.willLog(a.calls, a.tasks)}
    </Typography.Text>
  );
}

export function MatchView({
  previews,
  owners,
  canManage,
  fieldChoices,
  ownerPicks,
  effectivePolicy,
  tenantPolicy = 'fillBlanksOnly',
  summary,
  previewScope = 'changed',
  overwriteLocked,
  loading,
  onEditLead,
  active = true,
  dispatch,
}: MatchViewProps) {
  const { token } = antdTheme.useToken();
  const narrow = useNarrowViewport();
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  // Read off the LIVE previews (not the row closure) so a refetch while the
  // drawer is open re-renders it with fresh decisions instead of a stale copy.
  const detailsPerson = useMemo(
    () => previews.find((p) => p.personId === detailsId) ?? null,
    [previews, detailsId],
  );
  // Match stays mounted (display:none) across a stage-tab switch, but the
  // Drawer portals to document.body — close it explicitly when this view
  // stops being the active one, or it keeps floating over whatever is shown.
  useEffect(() => {
    if (!active) setDetailsId(null);
  }, [active]);

  function toggleChecked(personId: string) {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  }

  // Narrowed to the queue FIRST, split into columns second — which is what
  // makes the two columns a partition of `isInMatchQueue` by construction
  // rather than two filters that happen to add up today. `CrmViews` counts
  // that same predicate for the tab label, so the label cannot come apart
  // from the rows.
  //
  // Each column alphabetizes on its own; `.filter()` hands back a fresh array,
  // so the in-place sort never reaches the `previews` prop.
  const queueRows = useMemo(() => previews.filter(isInMatchQueue), [previews]);
  const newRows = useMemo(
    () => queueRows.filter((p) => p.bucket === 'new').sort(byDisplayName),
    [queueRows],
  );
  const alreadyRows = useMemo(
    () => queueRows.filter((p) => p.bucket !== 'new').sort(byDisplayName),
    [queueRows],
  );
  // Deliberately unsorted: a wire payload, not a display list.
  const assignableIds = previews
    .filter((p) => p.bucket !== 'ambiguous' && p.ownerRequired)
    .map((p) => p.personId);

  const ownerOptions = owners.map((o) => ({ value: o.id, label: ownerLabel(o) }));

  // Two gates, and BOTH are required.
  //
  // `previewScope === 'changed'` (fix round 2, Critical): `summary.collapsed`
  // is computed by the backend for every scope — under `scope: 'all'` it
  // reports what WOULD have been collapsed, while returning every one of
  // those rows. Reading the count alone would leave the line on screen after
  // "Show them", claiming N leads are hidden with all N drawn below it, and
  // leave its button dispatching `showAllPreview` into a state
  // `state.previewScope` is already in — same query key, no refetch, nothing
  // happens. The line belongs to the delta view only.
  //
  // `?? 0` then treats a `0` count and an `undefined` (older-backend
  // deploy-skew) identically: both render nothing, same zero-segment-omission
  // convention `SendView`'s disclosures already follow.
  const collapsedCount = previewScope === 'changed' ? (summary?.collapsed ?? 0) : 0;

  const bulkToolbar =
    canManage && assignableIds.length > 0 ? (
      <Space wrap>
        {/* Its OWN label, not the Leads tab's `assignSelectedTo` (spec 1 §6):
            this control sets the record owner in the customer's CRM, that
            one sets VendoIQ's internal `persons.assigned_to`. */}
        <Typography.Text strong>{CRM_COPY.setCrmOwnerForSelected}</Typography.Text>
        <HelpHint {...CRM_COPY.help.matchBulkAssign} testId="crm-help-match-bulk-assign" />
        <Select
          aria-label={CRM_COPY.setCrmOwnerForSelected}
          showSearch
          style={{ minWidth: 220 }}
          placeholder={CRM_COPY.ownerPickerPlaceholder}
          disabled={checkedIds.size === 0}
          options={ownerOptions}
          value={undefined}
          filterOption={(input, option) =>
            (option?.label as string | undefined)?.toLowerCase().includes(input.toLowerCase()) ?? false
          }
          onChange={(ownerId: string) => {
            dispatch({ type: 'bulkAssignOwner', personIds: Array.from(checkedIds), ownerId });
            setCheckedIds(new Set());
          }}
        />
      </Space>
    ) : undefined;

  return (
    <div data-testid="crm-match-view">
      <HintLine text={CRM_COPY.help.tabMatch.text} testId="crm-hint-tab-match" />
      <MergePolicyControl
        value={effectivePolicy ?? tenantPolicy}
        // The tenant default is unknown until the preview lands; an enabled
        // control showing the fallback could flip to Overwrite under the
        // operator, so it stays inert until then. While a preview is in
        // flight the columns say "Looking these leads up…"; with nobody in
        // the queue no preview ever runs and the control simply stays grey —
        // there is nothing for a policy to govern.
        disabled={!canManage || effectivePolicy === undefined}
        locked={overwriteLocked}
        onChange={(policy) => dispatch({ type: 'setPolicyOverride', policy })}
      />

      {/* Delta disclosure (p3 Task 5, spec 4 §5a) — ONE line above the
          columns, not a fourth panel or a tab-bar scope control (spec 4 §5
          rejected both explicitly). Only under the default `scope: 'changed'`,
          and only when that request actually collapsed something; the "show
          them" affordance is a re-request under `scope: 'all'`, not a
          client-side filter — the backend decides membership, and this line
          retires itself the moment the scope it describes is no longer the
          one on screen (see `collapsedCount` above — the backend keeps
          reporting `collapsed` under 'all', so the scope gate, not the
          count, is what ends the line). */}
      {collapsedCount > 0 && (
        <div data-testid="crm-match-collapsed-line" style={{ marginBottom: space.md }}>
          <Space size={space.xs} align="center" wrap>
            <Typography.Text type="secondary">{CRM_COPY.collapsedLine(collapsedCount)}</Typography.Text>
            <Button type="link" size="small" onClick={() => dispatch({ type: 'showAllPreview' })}>
              {CRM_COPY.showAllLink}
            </Button>
          </Space>
        </div>
      )}

      {/* 60/40, matched people first (gh-31 D10): the left column is the
          tab's subject and the right is final prep for the New people.
          `minmax(0, …)` tracks let both shrink instead of blowing the grid
          out on a narrow container. On a phone they stack, matched first. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: space.lg,
          alignItems: 'start',
        }}
      >
        <MatchColumn
          testId="match-column-already"
          title={CRM_COPY.alreadyInCrm}
          count={alreadyRows.length}
          emphasis="primary"
          emptyState={loading ? CRM_COPY.previewLoadingLine : CRM_COPY.alreadyInCrmEmptyState}
          hint={{ text: CRM_COPY.help.matchAlreadyGroup.text, testId: 'crm-hint-match-already' }}
          headerHelp={{ ...CRM_COPY.help.matchOwner, testId: 'crm-help-match-owner-already' }}
        >
          <Space direction="vertical" size={space.md} style={{ width: '100%' }}>
            {alreadyRows.map((p) => {
              if (p.bucket === 'ambiguous') {
                return (
                  <div key={p.personId} data-testid={`match-row-${p.personId}`}>
                    <Space direction="vertical" size={space.xs} style={{ width: '100%', minWidth: 0 }}>
                      <Typography.Text strong>{p.displayName}</Typography.Text>
                      {/* Deliberately no ActivityLine: this person matches
                          several CRM records, so the backend logs nothing
                          against them and promising a timeline entry here
                          would be a lie the send then fails to keep. */}
                      <Space size={space.xs} wrap>
                        <Typography.Text type="secondary">{CRM_COPY.ambiguousNote}</Typography.Text>
                        <HelpHint
                          {...CRM_COPY.help.matchAmbiguous}
                          testId={`crm-help-match-ambiguous-${p.personId}`}
                        />
                      </Space>
                    </Space>
                  </div>
                );
              }

              // Label and explanation are picked together so the hint can
              // never describe a record type other than the one on screen.
              const remoteType =
                p.remoteType === 'lead'
                  ? { tag: CRM_COPY.remoteTypeLabel.lead, help: CRM_COPY.help.matchLeadTag }
                  : p.remoteType === 'contact'
                    ? { tag: CRM_COPY.remoteTypeLabel.contact, help: CRM_COPY.help.matchContactTag }
                    : null;

              return (
                <div key={p.personId} data-testid={`match-row-${p.personId}`}>
                  <Space direction="vertical" size={space.xs} style={{ width: '100%', minWidth: 0 }}>
                    {/* `wrap`: name + type Tag + Details is three items on one
                        line, which no longer fits a half-width column. */}
                    <Space size={space.sm} wrap>
                      {canManage && p.ownerRequired && (
                        <Checkbox
                          checked={checkedIds.has(p.personId)}
                          onChange={() => toggleChecked(p.personId)}
                          aria-label={`Choose ${p.displayName}`}
                        />
                      )}
                      <Typography.Text strong>{p.displayName}</Typography.Text>
                      {remoteType && (
                        <>
                          <Tag>{remoteType.tag}</Tag>
                          <HelpHint
                            {...remoteType.help}
                            testId={`crm-help-match-type-${p.personId}`}
                          />
                        </>
                      )}
                      <Button
                        type="link"
                        size="small"
                        aria-haspopup="dialog"
                        onClick={() => setDetailsId(p.personId)}
                      >
                        {CRM_COPY.detailsToggle}
                      </Button>
                      <HelpHint
                        {...CRM_COPY.help.matchDetails}
                        testId={`crm-help-match-details-${p.personId}`}
                      />
                    </Space>
                    <ActivityLine preview={p} />
                    <OwnerPicker
                      owners={owners}
                      value={ownerPicks[p.personId]}
                      suggestedOwnerId={p.suggestedOwnerId}
                      currentOwner={p.currentOwner}
                      ownerRequired={p.ownerRequired}
                      ownerProvenance={p.ownerProvenance}
                      ownerUnverified={p.ownerUnverified}
                      disabled={!canManage}
                      onPick={(ownerId) => dispatch({ type: 'pickOwner', personId: p.personId, ownerId })}
                      ariaLabel={`Owner for ${p.displayName}`}
                    />
                    {/* Archived-owned row (2026-10-02 follow-up): a
                        `currentOwner` exists but can't act on it, so name
                        that plainly rather than leave the picker's warning
                        badge as the only signal something's off with the
                        owner already on the record. */}
                    {p.ownerRequired && p.currentOwner && (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: token.fontSizeSM }}
                        data-testid={`match-current-owner-unusable-${p.personId}`}
                      >
                        {CRM_COPY.currentOwnerUnusableNote(p.currentOwner.name)}
                      </Typography.Text>
                    )}
                  </Space>
                </div>
              );
            })}
          </Space>
        </MatchColumn>

        <MatchColumn
          testId="match-column-new"
          title={CRM_COPY.newHeader}
          count={newRows.length}
          emphasis="secondary"
          // "Nobody new" is a finding, not a default — it may only be said once
          // the look-up that would have found somebody has actually run.
          emptyState={loading ? CRM_COPY.previewLoadingLine : CRM_COPY.newEmptyState}
          hint={{ text: CRM_COPY.help.matchNewGroup.text, testId: 'crm-hint-match-new' }}
          headerHelp={{ ...CRM_COPY.help.matchOwner, testId: 'crm-help-match-owner-new' }}
          toolbar={bulkToolbar}
        >
          <Space direction="vertical" size={space.md} style={{ width: '100%' }}>
            {newRows.map((p) => (
              <div key={p.personId} data-testid={`match-row-${p.personId}`}>
                <Space size={space.sm} align="start" style={{ width: '100%' }}>
                  {canManage && p.ownerRequired && (
                    <Checkbox
                      checked={checkedIds.has(p.personId)}
                      onChange={() => toggleChecked(p.personId)}
                      aria-label={`Choose ${p.displayName}`}
                    />
                  )}
                  {/* `.ant-space-vertical` is `inline-flex`, so without an
                      explicit width this subtree sizes to its content and the
                      OwnerPicker pushes out of a half-width column. */}
                  <Space direction="vertical" size={space.xs} style={{ width: '100%', minWidth: 0 }}>
                    <Typography.Text strong>{p.displayName}</Typography.Text>
                    <ActivityLine preview={p} />
                    {p.similar && (
                      <Space size={space.xs} wrap>
                        <Typography.Text type="secondary">
                          {`${CRM_COPY.similarNotePrefix} ${p.similar} ${CRM_COPY.similarNoteSuffix}`}
                        </Typography.Text>
                        <HelpHint
                          {...CRM_COPY.help.matchSimilar}
                          testId={`crm-help-match-similar-${p.personId}`}
                        />
                      </Space>
                    )}
                    <OwnerPicker
                      owners={owners}
                      value={ownerPicks[p.personId]}
                      suggestedOwnerId={p.suggestedOwnerId}
                      currentOwner={p.currentOwner}
                      ownerRequired={p.ownerRequired}
                      ownerProvenance={p.ownerProvenance}
                      ownerUnverified={p.ownerUnverified}
                      disabled={!canManage}
                      onPick={(ownerId) => dispatch({ type: 'pickOwner', personId: p.personId, ownerId })}
                      ariaLabel={`Owner for ${p.displayName}`}
                    />
                    {isReady(p) && (
                      <Space size={space.xs} align="center">
                        <Tag color="success" data-testid={`match-ready-${p.personId}`}>
                          {CRM_COPY.readyToSendTag}
                        </Tag>
                        <HelpHint {...CRM_COPY.help.matchReadyTag} testId={`crm-help-match-ready-${p.personId}`} />
                      </Space>
                    )}
                  </Space>
                </Space>
              </div>
            ))}
          </Space>
        </MatchColumn>
      </div>

      <MatchDetailsDrawer
        person={detailsPerson}
        choices={detailsPerson ? fieldChoices[detailsPerson.personId] : undefined}
        canManage={canManage}
        effectivePolicy={effectivePolicy}
        overwriteLocked={overwriteLocked}
        onPick={(field, choice) => {
          if (detailsPerson) dispatch({ type: 'pickField', personId: detailsPerson.personId, field, choice });
        }}
        onClose={() => setDetailsId(null)}
        onEditOurs={
          onEditLead && detailsPerson
            ? (field, value) => onEditLead(detailsPerson.personId, { [field]: value })
            : undefined
        }
      />
    </div>
  );
}
